import { useEffect, useRef } from 'react';

export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    setTimeout(() => el.classList.add('hero-visible'), 100);
  }, []);

  const scrollToContact = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToOverview = (e: React.MouseEvent) => {
    e.preventDefault();
    document.getElementById('overview')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section
      className="relative min-h-screen flex items-center justify-center overflow-hidden"
      style={{ minHeight: '100vh' }}
    >
      {/* 배경 이미지 + 오버레이 */}
      <div className="absolute inset-0">
        <img
          src="https://public.readdy.ai/ai/img_res/34acff5f1313b20e5bbd405a75f256da.jpg"
          alt="석면해체 전문 작업 현장"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-green-950/60 via-green-950/45 to-green-950/25"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-green-950/55 via-transparent to-transparent"></div>
      </div>

      {/* 좌측 뱃지 + 타이틀 + 버튼 — PC만 표시 */}
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 pt-20 hidden md:flex items-center min-h-screen">
        <div
          ref={heroRef}
          className="max-w-2xl transition-all duration-1000 opacity-0 translate-y-8"
          style={{ '--tw-translate-y': '2rem' } as React.CSSProperties}
        >
          <div className="inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-4 py-2 mb-8">
            <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
            <span className="text-amber-300 text-sm font-medium tracking-wide">석면 해체·제거 전문 기업</span>
          </div>

          <h1 className="text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-tight mb-6">
            석면의 위험으로부터<br />
            <span className="text-amber-400">안전한 환경</span>을<br />
            만듭니다
          </h1>

          <p className="text-lg lg:text-xl text-white/80 leading-relaxed mb-10 max-w-xl">
            석면조사부터 해체·제거, 안전관리까지 18년 경력으로 한 번에 책임지는 한번에 맺음이 있는 석면전문기업 (주)소담이앤씨
          </p>

          <div className="flex flex-wrap gap-4">
            <button
              onClick={scrollToContact}
              className="bg-amber-500 hover:bg-amber-400 text-green-950 font-bold px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-base"
            >
              무료 상담 신청
            </button>
            <button
              onClick={scrollToOverview}
              className="border-2 border-white/50 hover:border-white text-white hover:bg-white/10 font-medium px-8 py-4 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-base flex items-center gap-2"
            >
              서비스 알아보기
              <i className="ri-arrow-down-line"></i>
            </button>
          </div>
        </div>
      </div>

      {/* 우측 하단 통계 — PC만 표시 */}
      <div className="absolute bottom-10 right-10 hidden md:flex flex-col gap-4 z-10">
        {[
          { number: '2,500+', label: '완료 현장' },
          { number: '100%', label: '적법 처리' },
          { number: '18년+', label: '업계 경력' },
        ].map((stat) => (
          <div
            key={stat.label}
            className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl px-6 py-4 text-center"
          >
            <div className="text-2xl font-bold text-amber-400">{stat.number}</div>
            <div className="text-xs text-white/70 mt-1 whitespace-nowrap">{stat.label}</div>
          </div>
        ))}
      </div>

      {/* 슬로건 + 본문 — PC: 화면 정중앙 오버레이 / 모바일: 전체 화면 중앙 단독 표시 */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 z-10">
        {/* 모바일 전용 상단 뱃지 */}
        <div className="md:hidden inline-flex items-center gap-2 bg-amber-500/20 border border-amber-400/40 rounded-full px-3 py-1.5 mb-5 pointer-events-none">
          <div className="w-1.5 h-1.5 bg-amber-400 rounded-full animate-pulse"></div>
          <span className="text-amber-300 text-xs font-medium tracking-wide">석면 해체·제거 전문 기업</span>
        </div>

        <div className="w-10 md:w-16 h-px bg-amber-400/80 mb-4 md:mb-6 pointer-events-none" />

        <p
          className="font-bold italic text-white tracking-wide leading-snug mb-4 md:mb-6 text-xl md:text-4xl pointer-events-none"
          style={{ fontFamily: "'Noto Serif KR', serif" }}
        >
          &ldquo;안전을 약속하고, 신뢰로 미래를 완성한다&rdquo;
        </p>

        <div className="w-10 md:w-16 h-px bg-amber-400/80 mb-4 md:mb-6 pointer-events-none" />

        <p
          className="text-white/85 leading-relaxed max-w-2xl text-xs md:text-lg pointer-events-none"
          style={{ fontFamily: "'Noto Serif KR', serif" }}
        >
          모든 현장에는 안전 기준과 절차가 존재합니다.<br />
          사고는 이를 지키지 않는 순간, 작은 방심에서 시작됩니다.
          <span className="hidden md:inline"><br />소담이앤씨는 사소한 요소 하나까지 관리하는 체계적인 안전 시스템을 구축하고 있습니다.<br />사소함이 곧 안전을 완성합니다.</span>
          <span className="md:hidden"><br />소담이앤씨는 체계적인 안전 시스템으로 사소한 요소 하나까지 관리합니다.</span>
        </p>

        {/* 모바일 전용 CTA 버튼 */}
        <div className="md:hidden flex flex-col gap-3 mt-6 w-full max-w-xs">
          <button
            onClick={scrollToContact}
            className="bg-amber-500 hover:bg-amber-400 text-green-950 font-bold px-6 py-3 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
          >
            무료 상담 신청
          </button>
          <button
            onClick={scrollToOverview}
            className="border border-white/50 text-white font-medium px-6 py-3 rounded-full transition-all duration-200 whitespace-nowrap cursor-pointer text-sm flex items-center justify-center gap-2"
          >
            서비스 알아보기
            <i className="ri-arrow-down-line"></i>
          </button>
        </div>
      </div>

      {/* 스크롤 유도 화살표 */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 animate-bounce z-10">
        <button onClick={scrollToOverview} className="cursor-pointer text-white/60 hover:text-white transition-colors">
          <i className="ri-arrow-down-s-line text-3xl"></i>
        </button>
      </div>
    </section>
  );
}
